import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FleetSharesLogo } from '../components/Logo';
import { LogOut, LayoutDashboard, Briefcase, Settings, Coins, TrendingUp, ShoppingBag, FileText, Menu, X, Trophy, ExternalLink } from 'lucide-react';

export const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Fleet', icon: LayoutDashboard },
    { path: '/portfolio', label: 'Portfolio', icon: Briefcase },
    { path: '/wallet', label: 'Wallet', icon: Coins },
    { path: '/dividends', label: 'Dividends', icon: TrendingUp },
    { path: '/marketplace', label: 'Trade', icon: ShoppingBag },
    { path: '/documents', label: 'Docs', icon: FileText },
    { path: '/leaderboard', label: 'Ranking', icon: Trophy },
  ];

  return (
    <header
      data-testid="main-header"
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[hsl(var(--border))]/40 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-36 md:h-44 lg:h-52">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 py-2">
            <FleetSharesLogo size="header" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200 ${
                  isActive(path)
                    ? 'bg-[hsl(var(--brand-primary))] text-white'
                    : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium text-sm">{label}</span>
              </Link>
            ))}
            {isAdmin() && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200 ${
                  isActive('/admin')
                    ? 'bg-[hsl(var(--brand-primary))] text-white'
                    : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span className="font-medium text-sm">Admin</span>
              </Link>
            )}
          </nav>

          {/* Right Side: User + Mobile Menu Button */}
          <div className="flex items-center gap-2">
            {/* Back to Hub Button */}
            <a
              href="https://yourultimatehub.com"
              data-testid="back-to-hub-btn"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="text-sm">Back to Hub</span>
            </a>

            {/* User Info (hidden on small screens) */}
            <Link
              to="/profile"
              data-testid="nav-profile"
              className="hidden sm:block text-right hover:bg-[hsl(var(--background-secondary))] px-3 py-2 rounded-lg transition-colors duration-200"
            >
              <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                {user?.name}
              </div>
              <div className="text-xs text-[hsl(var(--text-secondary))]">
                {user?.role === 'admin' ? 'Administrator' : 'Employee'}
              </div>
            </Link>

            {/* Logout Button */}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="p-2 rounded-lg hover:bg-[hsl(var(--background-secondary))] text-[hsl(var(--text-secondary))] transition-colors duration-200"
              aria-label="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-[hsl(var(--background-secondary))] text-[hsl(var(--text-secondary))] transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-[hsl(var(--border))]">
            <div className="grid grid-cols-3 gap-2">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg transition-colors duration-200 ${
                    isActive(path)
                      ? 'bg-[hsl(var(--brand-primary))] text-white'
                      : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{label}</span>
                </Link>
              ))}
              {isAdmin() && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg transition-colors duration-200 ${
                    isActive('/admin')
                      ? 'bg-[hsl(var(--brand-primary))] text-white'
                      : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="text-xs font-medium">Admin</span>
                </Link>
              )}
            </div>
            {/* Back to Hub - Mobile */}
            <a
              href="https://yourultimatehub.com"
              className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200"
            >
              <ExternalLink className="w-5 h-5" />
              <span>Back to Hub</span>
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};